import { z } from "zod";

/**
 * The values this client runs on, validated once at startup: three it cannot
 * run without, and the two optional analytics keys.
 *
 * A misconfigured deploy should fail on first paint with a sentence a person
 * can act on, not surface later as a fetch to `undefined/me/today`. Every
 * value here is public by construction — an API origin, a Supabase project
 * URL and its *publishable* key — so there is nothing in this file, or in the
 * built bundle, that needs protecting. Anything secret lives on the server.
 */
const Schema = z.object({
  VITE_API_BASE: z
    .url({ message: "VITE_API_BASE must be a full URL, e.g. https://api.tryantar.com" })
    .transform((u) => u.replace(/\/+$/, "")),
  VITE_SUPABASE_URL: z.url({ message: "VITE_SUPABASE_URL must be the Supabase project URL" }),
  VITE_SUPABASE_PUBLISHABLE_KEY: z
    .string()
    .min(1, "VITE_SUPABASE_PUBLISHABLE_KEY is required")
    .refine((k) => !k.startsWith("sb_secret_") && !k.startsWith("eyJ"), {
      message:
        "VITE_SUPABASE_PUBLISHABLE_KEY must be the publishable (sb_publishable_…) key, never a secret or service-role key — it ships in the browser bundle",
    }),
  // Optional: without it, nothing is measured and no Google script loads.
  VITE_GA_MEASUREMENT_ID: z
    .union([z.literal(""), z.string().regex(/^G-[A-Z0-9]+$/, "VITE_GA_MEASUREMENT_ID must be a Google Analytics id like G-XXXXXXX")])
    .optional(),
  // Optional: PostHog's project key. Public by design (it can only write
  // events into its own project), the same one the phone app ships with.
  VITE_POSTHOG_KEY: z
    .union([z.literal(""), z.string().regex(/^phc_[A-Za-z0-9]+$/, "VITE_POSTHOG_KEY must be a PostHog project key like phc_…")])
    .optional(),
});

export type Env = Readonly<{
  apiBase: string;
  supabaseUrl: string;
  supabasePublishableKey: string;
  /** Google Analytics measurement id, or null to measure nothing. */
  gaMeasurementId: string | null;
  /** PostHog project key, or null to run without product analytics. */
  posthogKey: string | null;
}>;

/** Parse an environment record into typed config, or throw with every problem listed. */
export function parseEnv(source: Record<string, string | undefined>): Env {
  const result = Schema.safeParse(source);
  if (!result.success) {
    const problems = result.error.issues
      .map((i) => `  - ${i.path.join(".") || "(env)"}: ${i.message}`)
      .join("\n");
    throw new Error(`Antar web is misconfigured:\n${problems}`);
  }
  const v = result.data;
  return Object.freeze({
    apiBase: v.VITE_API_BASE,
    supabaseUrl: v.VITE_SUPABASE_URL,
    supabasePublishableKey: v.VITE_SUPABASE_PUBLISHABLE_KEY,
    gaMeasurementId: v.VITE_GA_MEASUREMENT_ID || null,
    posthogKey: v.VITE_POSTHOG_KEY || null,
  });
}

/** The live configuration. Evaluated once; a bad deploy fails here, loudly. */
export const env: Env = parseEnv(import.meta.env as Record<string, string | undefined>);
